/* eslint-disable @typescript-eslint/camelcase */
export interface AuthenticateResponse {
  data: AuthenticateData;
  status: number;
}

export interface RestrictionsProps {
  min_length: number;
  max_length: number;
  required_numbers: boolean;
  required_upper_case: boolean;
  required_lower_case: boolean;
  required_special: boolean;
}

export interface RegisterPasswordState {
  readonly data: RegisterPasswordFields;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export interface ForgotPasswordState {
  readonly email: string;
  readonly isLoading: boolean;
}

export interface PasswordConfirmationProps {
  password: string;
  confirm_password: string;
}

export enum RolesEnum {
  superAdmin = 'superadmin',
  admin = 'admin',
  operator = 'operator',
  clientOperator = 'client_operator',
}

export interface AuthenticateData {
  token: string;
  operator_uuid: string;
  operator_first_name: string;
  operator_last_name: string;
  permissions: PermissionsData;
  available_regions: Array<{
    abbreviation: string;
    name: string;
    code: string;
    url: string;
  }>;
  logged_region: string;
  role: RolesEnum;
  mfa: MFAData;
}

export interface AuthenticateFields {
  email: string;
  password: string;
  mfa_required?: boolean;
  recaptcha_token: string;
}

export interface CompanyData {
  id: string;
  name: string;
  shortName: string;
}

export interface PartnerData {
  id: string;
  name: string;
  shortName: string;
  company?: {
    id: string;
    name: string;
  };
}

export interface MFAData {
  required: boolean;
  configured: boolean;
  secret: string;
  qrcode_url: string;
  totp_uri: string;
}

export interface PermissionsData {
  activate_user: boolean;
  activate_user_reset_password_at_login: boolean;
  attach_preparation_server: boolean;
  attach_support_server: boolean;
  block_environment_instances: boolean;
  block_user: boolean;
  can_accept_pending_extension: boolean;
  can_activate_environment_instances: boolean;
  can_apply_scaling_advisor: boolean;
  can_block_actions_partner_servers: boolean;
  can_block_actions_preparation_server: boolean;
  can_block_environment_instances: boolean;
  can_change_external_ad_password: boolean;
  can_clone_role: boolean;
  can_connect_instance: boolean;
  can_disable_server_monitoring_management: boolean;
  can_edit_company_alias: boolean;
  can_edit_clients_in_bulk: boolean;
  can_edit_env_scheduled_warnings: boolean;
  can_edit_environment_monitoring_settings: boolean;
  can_enable_server_monitoring_management: boolean;
  can_export_client_summary_list: boolean;
  can_export_environment_summary_list: boolean;
  can_export_partner_summary_list: boolean;
  can_export_user_summary_list: boolean;
  can_ignore_scaling_advisor: boolean;
  can_import_clients: boolean;
  can_import_users_in_all_clients: boolean;
  can_list_env_scheduled_warnings_details: boolean;
  can_manage_environment_status: boolean;
  can_register_environment_instances: boolean;
  can_restart_environment_instances: boolean;
  can_revert_scaling_advisor: boolean;
  can_simulate_applications: boolean;
  can_terminate_environment_instances: boolean;
  can_test_ldap: boolean;
  can_unblock_actions_partner_servers: boolean;
  can_unblock_actions_preparation_server: boolean;
  can_unblock_environment_instances: boolean;
  can_unregister_environment_instances: boolean;
  can_view_all_resources: boolean;
  can_view_environment_licenses: boolean;
  can_view_server_monitoring_status: boolean;
  cancel_image_publication_schedule: boolean;
  change_applications_attachment_environment: boolean;
  change_applications_name_environment: boolean;
  change_applications_state_environment: boolean;
  client_access_control: boolean;
  client_maintenance_history: boolean;
  clone_client: boolean;
  create_all_roles: boolean;
  create_api_token: boolean;
  create_applications_environment: boolean;
  create_models: boolean;
  create_image_publication_schedule: boolean;
  create_license_group: boolean;
  create_new_application: boolean;
  create_new_client: boolean;
  create_new_company: boolean;
  create_new_environment: boolean;
  create_new_operator: boolean;
  create_new_partner: boolean;
  create_new_user: boolean;
  create_new_web_application: boolean;
  create_self_api_token: boolean;
  create_user_theme_config: boolean;
  delete_all_roles: boolean;
  delete_api_token: boolean;
  delete_application: boolean;
  delete_client: boolean;
  delete_client_instance_sessions: boolean;
  delete_company: boolean;
  delete_environment: boolean;
  delete_environment_instance_sessions: boolean;
  delete_license_group: boolean;
  delete_models: boolean;
  delete_operator: boolean;
  delete_partner: boolean;
  delete_self_api_token: boolean;
  delete_self_operator: boolean;
  delete_user: boolean;
  delete_user_permanently: boolean;
  delete_user_theme_config: boolean;
  delete_user_theme_image: boolean;
  delete_web_application: boolean;
  detach_preparation_server: boolean;
  detach_support_server: boolean;
  disable_device_validation_bypass: boolean;
  disable_user_devices: boolean;
  edit_all_roles: boolean;
  edit_api_token: boolean;
  edit_application: boolean;
  edit_behavior: boolean;
  edit_block_instances_with_sessions_timeout: boolean;
  edit_client: boolean;
  edit_client_maintenance: boolean;
  edit_client_scheduled_warnings: boolean;
  edit_company_details: boolean;
  edit_env_auto_block_instances_maintenance: boolean;
  edit_env_auto_remove_user_session_maintenance: boolean;
  edit_environment_maintenance: boolean;
  edit_environment: boolean;
  edit_governance: boolean;
  edit_iframe_max_length: boolean;
  edit_license_group: boolean;
  edit_license_group_clients: boolean;
  edit_licenses: boolean;
  edit_models: boolean;
  edit_operator_details: boolean;
  edit_operator_role: boolean;
  edit_partner_details: boolean;
  edit_password_reqs: boolean;
  edit_scaling_advisor: boolean;
  edit_self_api_token: boolean;
  edit_self_operator_details: boolean;
  edit_user_details: boolean;
  edit_user_theme_config: boolean;
  edit_web_application: boolean;
  enable_device_validation_bypass: boolean;
  environment_maintenance_history: boolean;
  get_administrative_pin: boolean;
  get_client_maintenance: boolean;
  get_environment_maintenance: boolean;
  inactivate_user: boolean;
  inactivate_user_reset_password_at_login: boolean;
  include_application: boolean;
  list_all_companies: boolean;
  list_all_monitors: boolean;
  list_all_operator_token: boolean;
  list_all_operators: boolean;
  list_all_operators_audit_logs: boolean;
  list_all_partners: boolean;
  list_all_roles: boolean;
  list_all_users: boolean;
  list_applications: boolean;
  list_applications_environment: boolean;
  list_available_applications: boolean;
  list_client: boolean;
  list_client_instance_sessions: boolean;
  list_client_logs: boolean;
  list_client_preparation_instances: boolean;
  list_client_scheduled_warnings: boolean;
  list_client_sessions: boolean;
  list_client_support_instances: boolean;
  list_company_dashboard: boolean;
  list_company_details: boolean;
  list_company_users: boolean;
  list_dashboard_performance: boolean;
  list_env_auto_block_instances_details: boolean;
  list_env_auto_remove_user_session_details: boolean;
  list_environment: boolean;
  list_environment_cost: boolean;
  list_environment_dashboard: boolean;
  list_environment_details: boolean;
  list_environment_instance_sessions: boolean;
  list_environment_instances: boolean;
  list_environment_logs: boolean;
  list_iframe_max_length: boolean;
  list_image_publication_schedule: boolean;
  list_included_applications: boolean;
  list_license_groups: boolean;
  list_models: boolean;
  list_operator_details: boolean;
  list_operator_env_associations: boolean;
  list_operator_partner_associations: boolean;
  list_partner_dashboard: boolean;
  list_partner_details: boolean;
  list_partner_instances: boolean;
  list_partner_servers: boolean;
  list_preparation_servers: boolean;
  list_provider_resources: boolean;
  list_scaling_advisor_details: boolean;
  list_scaling_advisors: boolean;
  list_self_operator: boolean;
  list_self_operator_details: boolean;
  list_self_operator_token: boolean;
  list_session_history: boolean;
  list_support_servers: boolean;
  list_user_devices: boolean;
  list_user_theme_config: boolean;
  list_users: boolean;
  list_web_applications: boolean;
  migrate_environment: boolean;
  publish_image: boolean;
  publish_image_v2: boolean;
  publish_image_partner_servers: boolean;
  publish_new_image_partner_servers: boolean;
  publish_user_theme_config: boolean;
  remove_applications_environment: boolean;
  remove_included_application: boolean;
  reset_administrative_pin: boolean;
  reset_operator_mfa: boolean;
  reset_self_operator_mfa: boolean;
  reset_user_mfa: boolean;
  reset_user_password: boolean;
  start_partner_servers: boolean;
  start_preparation_server: boolean;
  start_support_server: boolean;
  stop_partner_servers: boolean;
  stop_preparation_server: boolean;
  stop_support_server: boolean;
  users_recovery: boolean;
  validate_instance_zero_trust: boolean;
}

export enum PermissionsEnum {
  activate_environment_instances = 'activate_environment_instances',
  activate_user = 'activate_user',
  activate_user_reset_password_at_login = 'activate_user_reset_password_at_login',
  attach_support_server = 'attach_support_server',
  block_environment_instances = 'block_environment_instances',
  block_user = 'block_user',
  can_accept_pending_extension = 'can_accept_pending_extension',
  can_apply_scaling_advisor = 'can_apply_scaling_advisor',
  can_block_actions_partner_servers = 'can_block_actions_partner_servers',
  can_block_actions_preparation_server = 'can_block_actions_preparation_server',
  can_block_environment_instances = 'can_block_environment_instances',
  can_change_external_ad_password = 'can_change_external_ad_password',
  can_clone_role = 'can_clone_role',
  can_connect_instance = 'can_connect_instance',
  can_disable_server_monitoring_management = 'can_disable_server_monitoring_management',
  can_edit_clients_in_bulk = 'can_edit_clients_in_bulk',
  can_edit_company_alias = 'can_edit_company_alias',
  can_edit_env_scheduled_warnings = 'can_edit_env_scheduled_warnings',
  can_edit_environment_monitoring_settings = 'can_edit_environment_monitoring_settings',
  can_enable_server_monitoring_management = 'can_enable_server_monitoring_management',
  can_export_client_summary_list = 'can_export_client_summary_list',
  can_export_environment_summary_list = 'can_export_environment_summary_list',
  can_export_partner_summary_list = 'can_export_partner_summary_list',
  can_export_user_summary_list = 'can_export_user_summary_list',
  can_ignore_scaling_advisor = 'can_ignore_scaling_advisor',
  can_manage_environment_status = 'can_manage_environment_status',
  can_restart_environment_instances = 'can_restart_environment_instances',
  can_revert_scaling_advisor = 'can_revert_scaling_advisor',
  can_simulate_applications = 'can_simulate_applications',
  can_terminate_environment_instances = 'can_terminate_environment_instances',
  can_test_ldap = 'can_test_ldap',
  can_unblock_actions_partner_servers = 'can_unblock_actions_partner_servers',
  can_unblock_actions_preparation_server = 'can_unblock_actions_preparation_server',
  can_unblock_environment_instances = 'can_unblock_environment_instances',
  can_unregister_environment_instances = 'can_unregister_environment_instances',
  can_view_all_resources = 'can_view_all_resources',
  can_view_environment_licenses = 'can_view_environment_licenses',
  can_view_server_monitoring_status = 'can_view_server_monitoring_status',
  cancel_image_publication_schedule = 'cancel_image_publication_schedule',
  change_applications_attachment_environment = 'change_applications_attachment_environment',
  change_applications_name_environment = 'change_applications_name_environment',
  change_applications_state_environment = 'change_applications_state_environment',
  client_access_control = 'client_access_control',
  client_maintenance_history = 'client_maintenance_history',
  clone_client = 'clone_client',
  create_api_token = 'create_api_token',
  create_applications_environment = 'create_applications_environment',
  create_image_publication_schedule = 'create_image_publication_schedule',
  create_license_group = 'create_license_group',
  create_new_application = 'create_new_application',
  create_new_client = 'create_new_client',
  create_new_company = 'create_new_company',
  create_new_environment = 'create_new_environment',
  create_new_operator = 'create_new_operator',
  create_new_partner = 'create_new_partner',
  create_new_user = 'create_new_user',
  create_new_web_application = 'create_new_web_application',
  create_preparation_server = 'create_preparation_server',
  create_support_server = 'create_support_server',
  create_user_theme_config = 'create_user_theme_config',
  delete_api_token = 'delete_api_token',
  delete_application = 'delete_application',
  delete_client = 'delete_client',
  delete_client_instance_sessions = 'delete_client_instance_sessions',
  delete_company = 'delete_company',
  delete_environment = 'delete_environment',
  delete_environment_instance_sessions = 'delete_environment_instance_sessions',
  delete_license_group = 'delete_license_group',
  delete_models = 'delete_models',
  delete_operator = 'delete_operator',
  delete_partner = 'delete_partner',
  delete_user = 'delete_user',
  delete_user_permanently = 'delete_user_permanently',
  delete_user_theme_config = 'delete_user_theme_config',
  delete_user_theme_image = 'delete_user_theme_image',
  delete_web_application = 'delete_web_application',
  detach_preparation_server = 'detach_preparation_server',
  detach_support_server = 'detach_support_server',
  disable_user_devices = 'disable_user_devices',
  disable_device_validation_bypass = 'disable_device_validation_bypass',
  edit_advanced_configs = 'edit_advanced_configs',
  edit_api_token = 'edit_api_token',
  edit_application = 'edit_application',
  edit_behavior = 'edit_behavior',
  edit_block_instances_with_sessions_timeout = 'edit_block_instances_with_sessions_timeout',
  edit_client = 'edit_client',
  edit_client_maintenance = 'edit_client_maintenance',
  edit_client_preparation_instances = 'edit_client_preparation_instances',
  edit_client_scheduled_warnings = 'edit_client_scheduled_warnings',
  edit_client_support_instances = 'edit_client_support_instances',
  edit_company_details = 'edit_company_details',
  edit_env_auto_block_instances_maintenance = 'edit_env_auto_block_instances_maintenance',
  edit_env_auto_remove_user_session_maintenance = 'edit_env_auto_remove_user_session_maintenance',
  edit_environment = 'edit_environment',
  edit_environment_maintenance = 'edit_environment_maintenance',
  edit_iframe_max_length = 'edit_iframe_max_length',
  edit_license_group = 'edit_license_group',
  edit_license_group_clients = 'edit_license_group_clients',
  edit_licenses = 'edit_licenses',
  edit_maintenance_periods = 'edit_maintenance_periods',
  edit_models = 'edit_models',
  edit_operator_client_associations = 'edit_operator_client_associations',
  edit_operator_company_associations = 'edit_operator_company_associations',
  edit_operator_details = 'edit_operator_details',
  edit_operator_email = 'edit_operator_email',
  edit_operator_env_associations = 'edit_operator_env_associations',
  edit_operator_partner_associations = 'edit_operator_partner_associations',
  edit_operator_role = 'edit_operator_role',
  edit_password_reqs = 'edit_password_reqs',
  edit_partner_details = 'edit_partner_details',
  edit_preparation_server = 'edit_preparation_server',
  edit_scaling_advisor = 'edit_scaling_advisor',
  edit_scheduler = 'edit_scheduler',
  edit_support_server = 'edit_support_server',
  edit_user_details = 'edit_user_details',
  edit_user_theme_config = 'edit_user_theme_config',
  edit_web_application = 'edit_web_application',
  enable_device_validation_bypass = 'enable_device_validation_bypass',
  environment_maintenance_history = 'environment_maintenance_history',
  get_administrative_pin = 'get_administrative_pin',
  get_client_maintenance = 'get_client_maintenance',
  get_environment_maintenance = 'get_environment_maintenance',
  include_application = 'include_application',
  inactivate_user = 'inactivate_user',
  inactivate_user_reset_password_at_login = 'inactivate_user_reset_password_at_login',
  list_all_clients_operator = 'list_all_clients_operator',
  list_all_companies = 'list_all_companies',
  list_all_companies_operator = 'list_all_companies_operator',
  list_all_environments_operator = 'list_all_environments_operator',
  list_all_operator_token = 'list_all_operator_token',
  list_all_operators = 'list_all_operators',
  list_all_operators_audit_logs = 'list_all_operators_audit_logs',
  list_all_partners = 'list_all_partners',
  list_all_partners_operator = 'list_all_partners_operator',
  list_all_roles = 'list_all_roles',
  list_applications_environment = 'list_applications_environment',
  list_applications = 'list_applications',
  list_available_applications = 'list_available_applications',
  list_client = 'list_client',
  list_client_instance_sessions = 'list_client_instance_sessions',
  list_client_logs = 'list_client_logs',
  list_client_preparation_instances = 'list_client_preparation_instances',
  list_client_sessions = 'list_client_sessions',
  list_client_support_instances = 'list_client_support_instances',
  list_company_details = 'list_company_details',
  list_company_users = 'list_company_users',
  list_dashboard_performance = 'list_dashboard_performance',
  list_env_auto_block_instances_details = 'list_env_auto_block_instances_details',
  list_env_auto_remove_user_session_details = 'list_env_auto_remove_user_session_details',
  list_environment = 'list_environment',
  list_environment_dashboard = 'list_environment_dashboard',
  list_environment_details = 'list_environment_details',
  list_environment_instance_sessions = 'list_environment_instance_sessions',
  list_environment_instances = 'list_environment_instances',
  list_environment_logs = 'list_environment_logs',
  list_iframe_max_length = 'list_iframe_max_length',
  list_image_publication_schedule = 'list_image_publication_schedule',
  list_included_applications = 'list_included_applications',
  list_license_groups = 'list_license_groups',
  list_models = 'list_models',
  list_operator_client_associations = 'list_operator_client_associations',
  list_operator_company_associations = 'list_operator_company_associations',
  list_operator_details = 'list_operator_details',
  list_operator_env_associations = 'list_operator_env_associations',
  list_operator_partner_associations = 'list_operator_partner_associations',
  list_partner_details = 'list_partner_details',
  list_partner_instances = 'list_partner_instances',
  list_partner_servers = 'list_partner_servers',
  list_preparation_servers = 'list_preparation_servers',
  list_scaling_advisor_details = 'list_scaling_advisor_details',
  list_scaling_advisors = 'list_scaling_advisors',
  list_session_history = 'list_session_history',
  list_support_servers = 'list_support_servers',
  list_user_devices = 'list_user_devices',
  list_user_theme_config = 'list_user_theme_config',
  list_users = 'list_users',
  list_web_applications = 'list_web_applications',
  migrate_environment = 'migrate_environment',
  publish_image = 'publish_image',
  publish_image_v2 = 'publish_image_v2',
  publish_image_partner_servers = 'publish_image_partner_servers',
  publish_new_image_partner_servers = 'publish_new_image_partner_servers',
  publish_user_theme_config = 'publish_user_theme_config',
  remove_applications_environment = 'remove_applications_environment',
  remove_client_sessions = 'remove_client_sessions',
  remove_included_application = 'remove_included_application',
  reset_administrative_pin = 'reset_administrative_pin',
  reset_operator_mfa = 'reset_operator_mfa',
  reset_user_mfa = 'reset_user_mfa',
  reset_user_password = 'reset_user_password',
  start_partner_servers = 'start_partner_servers',
  start_preparation_server = 'start_preparation_server',
  start_support_server = 'start_support_server',
  stop_partner_servers = 'stop_partner_servers',
  stop_preparation_server = 'stop_preparation_server',
  stop_support_server = 'stop_support_server',
  terminate_environment_instances = 'terminate_environment_instances',
  users_recovery = 'users_recovery',
  validate_instance_zero_trust = 'validate_instance_zero_trust',
}

export interface RegisterPasswordFields {
  code: string;
  password: string;
  confirmPassword: string;
}

export interface MfaData {
  required: boolean;
  configured: boolean;
  qrcode_url: string;
  secret: string;
}

export interface AuthenticateState {
  readonly data: AuthenticateData | null;
  readonly companySelected: CompanyData | null;
  readonly partnerSelected: PartnerData | null;
  readonly isLoading: boolean;
  readonly expiredSession: boolean;
  readonly isSuccess: boolean;
  readonly mfaChangeRegion: MfaData;
  readonly restrictions: RestrictionsProps;
}
